/**
 * The post-purchase setup flow — the stretch between "card charged" and
 * "website live", modelled like a delivery order so the client always knows
 * where their site is, who has the ball, and when it lands.
 *
 * This file is the single source of truth for the stages, their copy and the
 * shape of a status payload. The screen (components/Welcome) renders it; the
 * hook (hooks/useSetupStatus) fills it — from live endpoints where they exist,
 * from `buildMockStatus` in demo mode.
 */

import type { PlanId } from './pricing';

/* ------------------------------------------------------------------ */
/* Stages                                                              */
/* ------------------------------------------------------------------ */

export type SetupStage = 'payment' | 'building' | 'review' | 'launch' | 'live';

export const SETUP_STAGES: SetupStage[] = ['payment', 'building', 'review', 'launch', 'live'];

export interface StageDef {
  id: SetupStage;
  /** Short label on the tracker rail. */
  label: string;
  /** Big headline while this stage is current. */
  headline: string;
  /** One line under the headline. */
  detail: string;
  /** The sub-steps we tick off inside this stage — the "preparing your order" list. */
  steps: string[];
  /** True when the client, not us, is holding things up. */
  needsClient?: boolean;
}

export const STAGE_DEFS: Record<SetupStage, StageDef> = {
  payment: {
    id: 'payment',
    label: 'Confirmed',
    headline: "You're in. We've got your site.",
    detail: 'Payment confirmed. Your project is queued and starts within the hour.',
    steps: ['Payment confirmed', 'Account created', 'Project queued'],
  },
  building: {
    id: 'building',
    label: 'Building',
    headline: "We're building your website.",
    detail: 'Writing your pages, placing your photos, wiring up the forms.',
    steps: [
      'Pages written for your trade',
      'Photos and logo placed',
      'Contact form and click-to-call wired',
      'On-page SEO applied',
    ],
  },
  review: {
    id: 'review',
    label: 'Your review',
    headline: "Your site is up. Now let's make it yours.",
    detail:
      'Read it end to end and confirm your details. Then we put it on a domain of your own — that is the last step.',
    steps: ['Draft site published to your preview link', 'Waiting on your notes'],
    needsClient: true,
  },
  launch: {
    id: 'launch',
    label: 'Going live',
    headline: 'One thing left: your own domain.',
    detail:
      'Your site answers on a Foundlio address today. Point a name you own at it, or we find and register one for you.',
    steps: ['Domain connected', 'SSL certificate issued', 'Call tracking number attached', 'Search engines notified'],
    needsClient: true,
  },
  live: {
    id: 'live',
    label: 'Live',
    headline: 'Your website is live on your own domain.',
    detail: 'It works while you sleep. Everything from here happens in your dashboard.',
    steps: ['Site live on your domain', 'Analytics recording', 'Leads landing in your dashboard'],
  },
};

export function stageIndex(stage: SetupStage): number {
  const i = SETUP_STAGES.indexOf(stage);
  return i === -1 ? 0 : i;
}

/** 0…1 across the whole flow, with the current stage counted as half done. */
export function stageProgress(stage: SetupStage): number {
  if (stage === 'live') return 1;
  return (stageIndex(stage) + 0.5) / (SETUP_STAGES.length - 1);
}

/* ------------------------------------------------------------------ */
/* Payload                                                             */
/* ------------------------------------------------------------------ */

export type TaskOwner = 'you' | 'foundlio';
export type TaskStatus = 'todo' | 'doing' | 'done';

/**
 * Where the client's own domain has got to. Mirrors `DomainStatus` in
 * backend/src/services/setupService.ts — the server derives it, this file only
 * types it.
 *
 * `none` is the state every freshly-paid client is in, and the reason /welcome
 * exists: a site on `<slug>.foundlio.com` is a preview, not a launched website.
 */
export type DomainStatus = 'none' | 'pending' | 'deferred' | 'connected';

export interface SetupTask {
  id: string;
  title: string;
  description: string;
  owner: TaskOwner;
  status: TaskStatus;
  /** Button label when the client can act on it now. */
  cta?: string;
  /** In-app route or absolute URL for the CTA. */
  href?: string;
}

export interface SetupEvent {
  id: string;
  at: string;
  title: string;
  detail?: string;
  actor: 'team' | 'you' | 'system';
}

export interface SetupStatus {
  stage: SetupStage;
  /** When the current stage started — drives "12 min ago". */
  stageStartedAt: string;
  /** Best estimate for going live. Null once live. */
  etaAt: string | null;
  orderRef: string;
  businessName: string;
  /** The website this setup belongs to, when there is one yet. */
  websiteId: string | null;
  plan: PlanId;
  monthly: number;
  /** Whatever the website record calls home — may still be a Foundlio address. */
  domain: string | null;
  /** The client's own domain, once there is one. Null while on a Foundlio address. */
  customDomain: string | null;
  domainStatus: DomainStatus;
  previewUrl: string | null;
  screenshotUrl: string | null;
  /** Sub-steps of the current stage that are already done. */
  completedSteps: number;
  tasks: SetupTask[];
  events: SetupEvent[];
  /** True when the payload is fabricated for design/testing. */
  isMock: boolean;
}

/* ------------------------------------------------------------------ */
/* Shared reads — one definition for /welcome and the dashboard strip  */
/* ------------------------------------------------------------------ */

export function isSetupStage(value: unknown): value is SetupStage {
  return typeof value === 'string' && SETUP_STAGES.includes(value as SetupStage);
}

/**
 * A payload complete enough to draw a tracker from.
 *
 * Worth the guard: the status endpoint is the newest thing on this screen, and
 * a half-shaped response (an older backend, a proxy swallowing the body) would
 * otherwise render a stage-less strip on every dashboard.
 */
export function isRenderableSetupStatus(status: SetupStatus | null | undefined): status is SetupStatus {
  return Boolean(status && isSetupStage(status.stage) && Array.isArray(status.tasks));
}

/** True only when the site is serving on the client's own domain. */
export function isSetupComplete(status: SetupStatus): boolean {
  return status.stage === 'live';
}

export interface NextSetupAction {
  /** Imperative, short enough to sit on one line of a dashboard strip. */
  label: string;
  /** Who is holding things up. */
  owner: TaskOwner;
  /** Where the client goes to do it — a /welcome task panel where one exists. */
  href: string;
  /** The task this came from, when it came from one. */
  taskId?: string;
}

/**
 * The single next thing, chosen the way the client would choose it: whatever
 * they can act on now, with the domain last because it is the step that ends
 * the flow. Falls back to a truthful "we're on it" when the ball is ours.
 */
export function nextSetupAction(status: SetupStatus): NextSetupAction | null {
  if (isSetupComplete(status)) return null;

  const open = (status.tasks ?? []).filter((task) => task.owner === 'you' && task.status === 'todo');
  // Domain closes the flow, so it is the headline once nothing else is open —
  // but it never jumps the queue while they still owe us details or a read.
  const ordered = [...open].sort((a, b) => Number(a.id === 'domain') - Number(b.id === 'domain'));
  const next = ordered[0];

  if (next) {
    return {
      label: next.title,
      owner: 'you',
      href: `/welcome?task=${encodeURIComponent(next.id)}`,
      taskId: next.id,
    };
  }

  if (status.domainStatus === 'none' || status.domainStatus === 'deferred') {
    return { label: 'Connect or buy your domain', owner: 'you', href: '/welcome?task=domain', taskId: 'domain' };
  }

  return {
    label: status.domainStatus === 'pending' ? 'Waiting on DNS to land' : STAGE_DEFS[status.stage].label,
    owner: 'foundlio',
    href: '/welcome',
  };
}

/* ------------------------------------------------------------------ */
/* Mock — the "test" mode the screen is built against                  */
/* ------------------------------------------------------------------ */

const MOCK_BUSINESS = 'Valley Plumbing & Drain';

function minutesAgo(mins: number): string {
  return new Date(Date.now() - mins * 60_000).toISOString();
}

function hoursFromNow(hours: number): string {
  return new Date(Date.now() + hours * 3_600_000).toISOString();
}

const MOCK_EVENTS: Array<SetupEvent & { stage: SetupStage }> = [
  {
    id: 'e1',
    stage: 'payment',
    at: minutesAgo(96),
    title: 'Payment confirmed',
    detail: 'Managed plan · $199/mo · $0 setup',
    actor: 'system',
  },
  {
    id: 'e2',
    stage: 'payment',
    at: minutesAgo(95),
    title: 'Project queued',
    detail: 'Assigned to the build team',
    actor: 'system',
  },
  {
    id: 'e3',
    stage: 'building',
    at: minutesAgo(74),
    title: 'Build started',
    detail: 'Pulled your Google reviews, hours and service list',
    actor: 'team',
  },
  {
    id: 'e4',
    stage: 'building',
    at: minutesAgo(41),
    title: 'Pages written',
    detail: 'Home, Services, Service Areas, About, Contact',
    actor: 'team',
  },
  {
    id: 'e5',
    stage: 'review',
    at: minutesAgo(12),
    title: 'Draft ready for your review',
    detail: 'Preview link is live — tell us what to change',
    actor: 'team',
  },
  {
    id: 'e6',
    stage: 'launch',
    at: minutesAgo(4),
    title: 'Changes applied, going live',
    detail: 'Connecting valleyplumbingmadison.com',
    actor: 'team',
  },
  {
    id: 'e7',
    stage: 'live',
    at: minutesAgo(1),
    title: 'Your site is live',
    detail: 'SSL issued, analytics recording',
    actor: 'system',
  },
];

const MOCK_PREVIEW_HOST = 'valley-plumbing-madison.foundlio.com';
const MOCK_CUSTOM_DOMAIN = 'valleyplumbingmadison.com';

/** The demo walks a client who only gets their own domain at the launch step. */
function mockDomainStatus(stage: SetupStage): DomainStatus {
  if (stage === 'live') return 'connected';
  if (stage === 'launch') return 'pending';
  return 'none';
}

function mockTasks(stage: SetupStage): SetupTask[] {
  const done = (s: SetupStage) => stageIndex(stage) > stageIndex(s);
  const domainStatus = mockDomainStatus(stage);

  return [
    {
      id: 'confirm-details',
      title: 'Confirm your business details',
      description: 'Hours, service area and the phone you want calls to ring on.',
      owner: 'you',
      status: done('payment') ? 'done' : 'todo',
      cta: 'Confirm details',
      href: '/dashboard/account/profile',
    },
    {
      id: 'photos',
      title: 'Send us your photos',
      description: 'Job photos beat stock every time. Five good ones is plenty.',
      owner: 'you',
      status: done('building') ? 'done' : stage === 'building' ? 'todo' : 'todo',
      cta: 'Upload photos',
      href: '/dashboard/websites',
    },
    {
      id: 'review-draft',
      title: 'Review your draft site',
      description: 'Read it end to end and tell us anything that reads wrong.',
      owner: 'you',
      status: done('review') ? 'done' : stage === 'review' ? 'todo' : 'doing',
      cta: 'Open preview',
      href: '#preview',
    },
    {
      id: 'domain',
      title: domainStatus === 'connected' ? 'Your domain' : 'Connect or buy your domain',
      description:
        domainStatus === 'connected'
          ? `${MOCK_CUSTOM_DOMAIN} is on file. We point it at your site and issue the certificate.`
          : domainStatus === 'pending'
            ? 'We have the name. Nothing else to do while the DNS and certificate land.'
            : 'Your site answers on a Foundlio address today. Point a name you already own at it, or we find and register one for you.',
      owner: 'you',
      status: domainStatus === 'connected' ? 'done' : domainStatus === 'none' ? 'todo' : 'doing',
      cta: 'Set up domain',
      href: '/dashboard/account/domain',
    },
    {
      id: 'gbp',
      title: 'Google Business Profile',
      description: 'We claim and optimise it so the site and the map listing agree.',
      owner: 'foundlio',
      status: done('launch') ? 'done' : 'doing',
    },
  ];
}

/** A complete, believable status payload for any stage. */
export function buildMockStatus(stage: SetupStage): SetupStatus {
  const idx = stageIndex(stage);
  const def = STAGE_DEFS[stage];
  const domainStatus = mockDomainStatus(stage);
  const customDomain = domainStatus === 'connected' ? MOCK_CUSTOM_DOMAIN : null;

  return {
    stage,
    stageStartedAt: minutesAgo(stage === 'live' ? 1 : 12),
    etaAt: stage === 'live' ? null : hoursFromNow((SETUP_STAGES.length - 1 - idx) * 22),
    orderRef: 'FDL-4127',
    businessName: MOCK_BUSINESS,
    websiteId: 'demo-website',
    plan: 'professional',
    monthly: 199,
    domain: customDomain ?? MOCK_PREVIEW_HOST,
    customDomain,
    domainStatus,
    // The draft link only exists once there is a draft to look at.
    previewUrl: idx >= stageIndex('review') ? `https://${MOCK_PREVIEW_HOST}` : null,
    screenshotUrl: null,
    // Nothing in "Going live" is true before the domain is connected.
    completedSteps:
      stage === 'live' ? def.steps.length : stage === 'launch' ? 0 : Math.max(1, def.steps.length - 1),
    tasks: mockTasks(stage),
    events: MOCK_EVENTS.filter((e) => stageIndex(e.stage) <= idx)
      .map(({ stage: _stage, ...event }) => event)
      .reverse(),
    isMock: true,
  };
}
